import { LockIcon } from 'lucide-react';

import type { IdentityState } from '@kit/shared/identity';

import { cn } from '../lib/utils';
import { Badge } from '../shadcn/badge';
import { Trans } from './trans';

/**
 * Renders a person whose name we cannot simply print.
 *
 * One component so the decision is made once. Before this, the same situation
 * produced "Inconnu" in chat, a hardcoded English 'Unknown' in the audition
 * loaders, a silently dropped row on the partner surface, and the literal
 * string "null" wherever a first name was missing.
 *
 * `restricted` never shows a name — see the IdentityState docs. It renders a
 * lock, because "we are not allowed to look" is a different and more honest
 * statement than "we do not know who this is".
 */
export function IdentityLabel({
  identity,
  className,
}: {
  identity: IdentityState;
  className?: string;
}) {
  if (identity.status === 'present') {
    return <span className={className}>{identity.displayName}</span>;
  }

  const muted = cn('text-muted-foreground', className);

  if (!identity.displayName) {
    return (
      <span className={cn(muted, 'inline-flex items-center gap-1 italic')}>
        {identity.status === 'restricted' ? (
          <LockIcon className="h-3 w-3" aria-hidden />
        ) : null}
        <Trans i18nKey={identity.labelKey} />
      </span>
    );
  }

  // A retained name plus the reason it is annotated — the shape an
  // organisation sees on an offer it made to someone who has since left.
  return (
    <span className={cn('inline-flex items-center gap-2', className)}>
      <span className={muted}>{identity.displayName}</span>
      <IdentityStatusBadge identity={identity} />
    </span>
  );
}

/** The pill on its own, for tables that lay out name and status separately. */
export function IdentityStatusBadge({ identity }: { identity: IdentityState }) {
  if (!identity.showBadge) return null;

  // `destructive` matches the existing banned-user pill on the admin account
  // page, which is the repo's established "this person is in a terminal state"
  // signal. `outline` for the merely-unreadable states, which are not terminal.
  const variant =
    identity.status === 'deleted'
      ? ('destructive' as const)
      : ('outline' as const);

  return (
    <Badge variant={variant} className="text-[10px] font-normal">
      <Trans i18nKey={identity.labelKey} />
    </Badge>
  );
}
